/**
 * Quem trabalha em qual unidade (spec 019, decisão 5 + pedido de 20/10/2026:
 * "habilitar o acesso a qualquer das unidades para qualquer usuário, desde
 * que autorizado pelo admin ou dono").
 *
 * Acessar uma unidade = poder escolhê-la como unidade atual (Topbar, Agenda,
 * Liberar agenda) e marcar paciente nela (formulário).
 *
 * - Admin e dono acessam todas, sempre.
 * - `professionals/{uid}.unitAccess` é a autorização, gravada só por admin
 *   ou dono (firestore rules recusam a pessoa mexer na própria):
 *     - ausente → padrão: as unidades onde a pessoa atende (`unitIds`);
 *       sem nenhuma, a Sede;
 *     - `'todas'` → todas, inclusive as que forem criadas, e o "Todas as
 *       unidades" da Agenda;
 *     - lista de ids → só essas.
 * - Unidade inativa não conta; se não sobrar nenhuma, fica a Sede (ou a
 *   primeira ativa) — ninguém fica sem unidade.
 */
import { Role } from '@/types/auth';
import { SEDE_UNIT_ID } from './salas';

export const ALL_UNITS_ACCESS = 'todas' as const;

/** O que está gravado em `unitAccess`. `undefined` = padrão. */
export type UnitAccessGrant = typeof ALL_UNITS_ACCESS | string[] | undefined;

/** Lê `unitAccess` do doc do profissional. Lixo vira o padrão. */
export function parseUnitAccess(raw: unknown): UnitAccessGrant {
  if (raw === ALL_UNITS_ACCESS) return ALL_UNITS_ACCESS;
  if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string' && !!v);
  return undefined;
}

/** Acessa todas as unidades (e vê "Todas as unidades" na Agenda e na
 *  Liberar agenda)? Admin e dono sempre (decisão 5); os outros, se admin/dono
 *  autorizou "todas". */
export function hasAllUnitsAccess(
  role: Role | string | null | undefined,
  isOwner: boolean,
  grant: UnitAccessGrant,
): boolean {
  return isOwner || role === Role.ADMIN || grant === ALL_UNITS_ACCESS;
}

/**
 * Unidades que a pessoa acessa, na ordem de `activeUnitIds`.
 *
 * `activeUnitIds` = unidades ativas (na ordem das telas); `attendUnitIds` =
 * onde ela atende (`professionals.unitIds`); `allAccess` = `hasAllUnitsAccess`.
 */
export function accessibleUnitIds(opts: {
  activeUnitIds: readonly string[];
  grant: UnitAccessGrant;
  attendUnitIds: readonly string[];
  allAccess: boolean;
}): string[] {
  const { activeUnitIds, grant, attendUnitIds, allAccess } = opts;
  if (allAccess) return [...activeUnitIds];
  const allowed = Array.isArray(grant) ? grant : attendUnitIds;
  const ids = activeUnitIds.filter((id) => allowed.includes(id));
  if (ids.length > 0) return ids;
  const home = activeUnitIds.includes(SEDE_UNIT_ID) ? SEDE_UNIT_ID : activeUnitIds[0];
  return home ? [home] : [];
}
